import { useCallback, useMemo } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import { useHttpClient } from '../../client/HttpClientContext';
import { getPropertyDefinitions } from '../../propertyDefinitions/getPropertyDefinitions';
import { useAsyncFunction } from '../../utils/hooks/useAsyncFunction';
import { keyBy } from '../../utils/keyBy';
import { usePropertyDefinitionOverride } from '../context/PropertyDefinitionOverrideContext';
import { useObjectTypeId } from './useObjectTypeId';
import { useProperty } from './useProperty';
const keyByName = memoizeOne(properties => keyBy(properties, ({
  property: {
    name
  }
}) => name));
export const usePropertyDefinitionByName = (objectTypeId, options = {
  skip: false
}) => {
  const propertyDefinitionOverride = usePropertyDefinitionOverride();
  const httpClient = useHttpClient();
  const getDefinitions = useCallback(() => {
    return getPropertyDefinitions({
      objectTypeId,
      httpClient
    }).then(keyByName);
  }, [httpClient, objectTypeId]);
  const {
    data: propertyDefinitionsByName = {},
    error,
    loading
  } = useAsyncFunction(getDefinitions, options);
  if (propertyDefinitionOverride) {
    propertyDefinitionsByName[propertyDefinitionOverride.property.name] = propertyDefinitionOverride;
  }
  return {
    propertyDefinitionsByName,
    error,
    loading
  };
};
export const usePropertyDefinition = ({
  skip
} = {}) => {
  const property = useProperty();
  const objectTypeId = useObjectTypeId();
  const {
    propertyDefinitionsByName,
    error,
    loading
  } = usePropertyDefinitionByName(objectTypeId, {
    skip
  });
  const propertyDefinition = propertyDefinitionsByName === null || propertyDefinitionsByName === void 0 ? void 0 : propertyDefinitionsByName[property.name];
  return {
    propertyDefinition,
    error,
    loading
  };
};
export const usePropertyDefinitionsByObjectTypeId = (objectTypeIds, options = {
  skip: false
}) => {
  const httpClient = useHttpClient();
  const stringifiedObjectTypeIds = objectTypeIds.join(',');

  // getPropertyDefinitions caches, but we don't want to repeat the keying operation, so
  // we maintain a local cache to avoid the extra work.
  const definitionsCache = useMemo(() => new Map(), []);
  const getDefinitions = useCallback(() => {
    const uncachedIds = objectTypeIds.filter(id => !definitionsCache.has(id));
    return Promise.all(uncachedIds.map(objectTypeId => getPropertyDefinitions({
      objectTypeId,
      httpClient
    }).then(definitions => {
      definitionsCache.set(objectTypeId, keyByName(definitions));
    }))).then(() => {
      return objectTypeIds.reduce((acc, objectTypeId) => {
        acc[objectTypeId] = definitionsCache.get(objectTypeId);
        return acc;
      }, {});
    });
    // we shallow compare the object type ids to avoid breaking the memoization when array reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [httpClient, stringifiedObjectTypeIds, definitionsCache]);
  return useAsyncFunction(getDefinitions, options);
};